import { XMLParser } from "fast-xml-parser";
import { mapXsdType } from "../type-map.js";
const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
    removeNSPrefix: true,
});
export function parseXsd(content) {
    const doc = parser.parse(content);
    const elements = collectSequenceElements(doc);
    if (elements.length === 0)
        throw new Error("no element sequence found in XSD");
    return elements.map((el, i) => {
        const minOccurs = el["@_minOccurs"];
        const restriction = el?.simpleType;
        const restrictionDetail = restriction?.restriction;
        const maxLength = restrictionDetail?.maxLength?.["@_value"];
        const pattern = restrictionDetail?.pattern?.["@_value"];
        const sourceType = String(el["@_type"] ?? restrictionDetail?.["@_base"] ?? "");
        const name = String(el["@_name"] ?? "");
        return {
            name,
            order: i + 1,
            type: mapXsdType(sourceType),
            required: minOccurs === undefined ? true : Number(minOccurs) >= 1,
            sourceType: sourceType || undefined,
            ...(maxLength || pattern
                ? {
                    constraints: {
                        maxLength: maxLength ? Number(maxLength) : undefined,
                        pattern: pattern ? String(pattern) : undefined,
                    },
                }
                : {}),
        };
    });
}
// Depth-first search for the first complexType/sequence and return its element children (always an array).
function collectSequenceElements(node) {
    if (node == null || typeof node !== "object")
        return [];
    const nodeRec = node;
    const complexType = nodeRec.complexType;
    const sequence = complexType?.sequence;
    const seq = sequence?.element;
    if (seq) {
        if (Array.isArray(seq))
            return seq;
        if (typeof seq === "object" && seq !== null)
            return [seq];
    }
    for (const v of Object.values(nodeRec)) {
        const found = collectSequenceElements(v);
        if (found.length)
            return found;
    }
    return [];
}
// ─── Structure parser (preserves nesting) ────────────────────────────────────
/**
 * Parses an XSD and returns an `ExportFormatStructureNode[]` tree that
 * preserves the nesting the XSD already encodes. Unlike `parseXsd` (which
 * flattens to `ImportedField[]`), this emits `group` / `collection` container
 * nodes whose `children` recursively hold sub-elements, and `field` leaf nodes
 * for primitive-typed elements.
 *
 * The document's root `xs:element` becomes the single top-level node.
 * All collections get `cardinality: { min: 0 }` (uniform Slice 3a default).
 */
export function parseXsdToStructure(content) {
    const doc = parser.parse(content);
    const schema = (doc.schema ?? doc["xs:schema"] ?? doc);
    // Build a lookup for top-level named complexTypes so we can resolve type= refs.
    const namedComplexTypes = buildNamedComplexTypeMap(schema);
    // The root xs:element(s) of the schema.
    const rawEl = schema.element;
    if (rawEl == null)
        throw new Error("no root element found in XSD");
    // Finding #3: handle both a single root element and an array of root elements.
    const rootEls = Array.isArray(rawEl)
        ? rawEl
        : [rawEl];
    return rootEls.map((el) => buildStructureNode(el, namedComplexTypes, new Set()));
}
/** Build a map of { typeName -> complexType object } from top-level named complexTypes. */
function buildNamedComplexTypeMap(schema) {
    const map = new Map();
    const raw = schema.complexType;
    const types = Array.isArray(raw)
        ? raw
        : raw != null && typeof raw === "object"
            ? [raw]
            : [];
    for (const ct of types) {
        const name = ct["@_name"];
        if (typeof name === "string")
            map.set(name, ct);
    }
    return map;
}
/** Extract the sequence element array from a complexType object (inline or resolved). */
function getSequenceElements(complexType) {
    const sequence = complexType.sequence;
    if (!sequence)
        return [];
    const seq = sequence.element;
    if (Array.isArray(seq))
        return seq;
    if (seq != null && typeof seq === "object")
        return [seq];
    return [];
}
/** Resolve the complexType for an element — inline or via a type= name reference. */
function resolveComplexType(el, namedComplexTypes) {
    // Inline complexType.
    if (el.complexType != null && typeof el.complexType === "object") {
        return el.complexType;
    }
    // Named type= reference.
    const typeAttr = el["@_type"];
    if (typeof typeAttr === "string") {
        // Strip namespace prefix (e.g. "xs:Invoice" → "Invoice").
        const localName = typeAttr.includes(":") ? typeAttr.split(":").pop() : typeAttr;
        const resolved = namedComplexTypes.get(localName);
        if (resolved)
            return resolved;
    }
    return null;
}
/**
 * Recursively build a structure node for one xs:element.
 *
 * @param seen  Set of NAMED complexType names currently on the call stack —
 *              used to detect cycles (finding #1). Inline (anonymous) complexTypes
 *              are not tracked here because they are structurally unique.
 */
function buildStructureNode(el, namedComplexTypes, seen) {
    const name = String(el["@_name"] ?? "");
    const maxOccurs = el["@_maxOccurs"];
    const minOccurs = el["@_minOccurs"];
    // Finding #1: determine if this element resolves to a NAMED complexType.
    const typeAttr = el["@_type"];
    const resolvedTypeName = typeof typeAttr === "string"
        ? (typeAttr.includes(":") ? typeAttr.split(":").pop() : typeAttr)
        : null;
    // Cycle guard: if we've already begun expanding this named type on the current
    // path, stop here and emit a container with empty children rather than recurse.
    if (resolvedTypeName !== null && seen.has(resolvedTypeName)) {
        const isCollection = maxOccurs === "unbounded" ||
            (typeof maxOccurs === "string" && Number(maxOccurs) > 1) ||
            // Finding #2: fast-xml-parser may return numeric attribute values.
            (typeof maxOccurs === "number" && maxOccurs > 1);
        if (isCollection) {
            return { name, type: "collection", cardinality: { min: 0 }, children: [] };
        }
        return { name, type: "group", children: [] };
    }
    const complexType = resolveComplexType(el, namedComplexTypes);
    if (complexType !== null) {
        // Push this named type onto the seen set before recursing into its children.
        const newSeen = new Set(seen);
        if (resolvedTypeName !== null)
            newSeen.add(resolvedTypeName);
        // Container node.
        const children = getSequenceElements(complexType).map((child) => buildStructureNode(child, namedComplexTypes, newSeen));
        // Finding #2: handle numeric maxOccurs (e.g. maxOccurs="3" parsed as number 3).
        const isCollection = maxOccurs === "unbounded" ||
            (typeof maxOccurs === "string" && Number(maxOccurs) > 1) ||
            (typeof maxOccurs === "number" && maxOccurs > 1);
        if (isCollection) {
            return { name, type: "collection", cardinality: { min: 0 }, children };
        }
        return { name, type: "group", children };
    }
    // Leaf / field node.
    const sourceType = String(el["@_type"] ?? "");
    const dataType = mapXsdType(sourceType);
    const required = minOccurs === undefined ? true : minOccurs !== "0";
    const allowEmpty = !required;
    // maxLength restriction — may live inside a simpleType/restriction/maxLength.
    const simpleType = el.simpleType;
    const restriction = simpleType?.restriction;
    const maxLengthVal = restriction?.maxLength?.["@_value"];
    const length = maxLengthVal !== undefined ? Number(maxLengthVal) : undefined;
    const node = { name, type: "field", dataType, required, allowEmpty };
    if (length !== undefined)
        node.length = length;
    return node;
}
//# sourceMappingURL=xsd.js.map